import styled from 'styled-components';
import React, { useCallback, useContext } from 'react';
import { Menu, MenuItem } from '@material-ui/core';
import {
  AiContext,
  RenderActionProps,
  useActionItem,
} from '@workboard/action-item';
import { SvgIcon } from '@mui/material';

import { getFeatureFlag } from '../../utils/getFeatureFlag';
import { use1on1Modal } from './use1on1Modal';
import { useConvertSubAction } from './useConvertSubAction';
import { useConvertToSubAction } from './useConvertToSubAction';
import { useCopyModal } from './useCopyModal';
import { useDeleteModal } from './useDeleteModal';
import { useGiveBadge } from './useGiveBadge';
import { useMeetingModal } from './useMeetingModal';
import { useMirrorModal } from './useMirrorModal';
import { usePingForStatus } from './usePingForStatus';
import { usePushToApp } from './usePushToApp';
import { useRemindMeModal } from './useRemindMeModal';
import { useRepeatModal } from './useRepeatModal';
import { useRequestFeedback } from './useRequestFeedback';
import * as testIds from './testIds';
import Icon from '../../Icon';
import FeedbackSvg from '../../../../vectors/give_feedback_new.svg';
import RequestFeedbackSvg from '../../../../vectors/request_feedback.svg';
import { useTranslation } from 'react-i18next';

const MenuItemWrapper = styled.div`
  gap: 14px;
  display: flex;
  align-items: center;
`;

const Actions = ({ anchorEl, open, close }: RenderActionProps) => {
  const { t } = useTranslation();
  const aiContext = useContext(AiContext);
  const { id, closeDetail } = aiContext;

  const { actionItem, refetch } = useActionItem(id);
  const aiId = actionItem?.id ?? id;

  const activityLabel = actionItem?.typeAttributes?.label ?? 'action';
  const activityTypeName = activityLabel.toLowerCase();

  const [canConvertToAi, convertToAi] = useConvertSubAction(aiId, refetch);
  const [canConvertToSubAction, convertToSubAction] = useConvertToSubAction(
    aiId,
    () => setTimeout(() => anchorEl.focus(), 0)
  );
  const [canCopy, showCopyModal] = useCopyModal(aiId);
  const [canDelete, showDeleteAiModal] = useDeleteModal(aiId, closeDetail);
  const [canGiveBadge, giveBadge] = useGiveBadge(aiId, 'ACTION_ITEM_DETAIL');
  const [canMeet, showMeetingsModal] = useMeetingModal(aiId);
  const [canMeet1on1, show1on1Modal] = use1on1Modal(aiId);
  const [canMirror, showMirrorAiModal] = useMirrorModal(aiId);
  const [canPingForStatus, pingForStatus] = usePingForStatus(aiId);
  const [integrations, pushToApp] = usePushToApp(aiId, activityLabel);
  const [canRemind, showRemindMeModal] = useRemindMeModal(aiId);
  const [canRepeat, showRepeatAiModal] = useRepeatModal(aiId);
  const [canRequestFeedback, requestFeedback] = useRequestFeedback(aiId, 'ACTION_ITEM_DETAIL');

  const getCallback = useCallback(
    <Callback extends (e: React.MouseEvent<unknown>) => any>(
      callback: Callback
    ): ((e: React.MouseEvent<unknown>) => ReturnType<Callback>) =>
      (e: React.MouseEvent<unknown>) => {
        close();
        return callback(e);
      },
    [close]
  );

  return (
    <Menu
      keepMounted
      anchorEl={anchorEl}
      open={open}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      getContentAnchorEl={null}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
        className: 'ai-menu-wrapper',
        style: {
          minWidth: '200px',
        },
      }}
      disablePortal
    >
      {canConvertToSubAction && (
        <MenuItem
          disableRipple
          onClick={getCallback(convertToSubAction)}
          data-testid={testIds.convertToSubAction}
        >
          <MenuItemWrapper>
            <Icon name="subaction" />
            <span>{t('Convert to subtask')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {canCopy && (
        <MenuItem
          disableRipple
          onClick={getCallback(showCopyModal)}
          data-testid={testIds.showCopyModal}
        >
          <MenuItemWrapper>
            <Icon name="copy" />
            <span>{t('Copy')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {canRepeat && (
        <MenuItem
          disableRipple
          onClick={getCallback(showRepeatAiModal)}
          data-testid={testIds.showRepeatModal}
        >
          <MenuItemWrapper>
            <Icon name="repeat" />
            <span>{t('Repeat')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {canConvertToAi && (
        <MenuItem
          disableRipple
          onClick={getCallback(convertToAi)}
          data-testid={testIds.convertToAi}
        >
          <MenuItemWrapper>
            <Icon name="convertToAi" />
            <span>{t("meeting.convertToAction", { activityTypeName })}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {canGiveBadge && !getFeatureFlag('isFeedbackDisabledCompletely') && (
        <MenuItem
          disableRipple
          onClick={getCallback(giveBadge)}
          data-testid={testIds.giveBadge}
        >
          <MenuItemWrapper>
            {
              canRequestFeedback ?
                <SvgIcon component={FeedbackSvg} htmlColor='#687587' sx={{ translate: '2px 4px' }} /> :
                <Icon name="goblet" />
            }
            <span>{t('Give Feedback')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}
      {
        canRequestFeedback && !getFeatureFlag('isFeedbackDisabledCompletely') && (
          <MenuItem
            disableRipple
            onClick={getCallback(requestFeedback)}
            data-testid={testIds.requestFeedback}
          >
            <MenuItemWrapper>
              <SvgIcon component={RequestFeedbackSvg} htmlColor='#687587' sx={{ translate: '2px 4px' }} />
              <span>{t('Request feedback')}</span>
            </MenuItemWrapper>
          </MenuItem>
        )
      }

      {canMeet && (
        <MenuItem
          disableRipple
          onClick={getCallback(showMeetingsModal)}
          data-testid={testIds.showMeetingsModal}
        >
          <MenuItemWrapper>
            <Icon name="addToMeeting" />
            <span>{t('Add to meeting')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {canMeet1on1 && (
        <MenuItem
          disableRipple
          onClick={getCallback(show1on1Modal)}
          data-testid={testIds.show1on1Modal}
        >
          <MenuItemWrapper>
            <Icon name="addTo1on1" />
            <span>{t('Add to 1on1')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {canRemind && (
        <MenuItem
          disableRipple
          onClick={getCallback(showRemindMeModal)}
          data-testid={testIds.showRemindMeModal}
        >
          <MenuItemWrapper>
            <Icon name="remind" />
            <span>{t('Remind me')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {canMirror && (
        <MenuItem
          disableRipple
          onClick={getCallback(showMirrorAiModal)}
          data-testid={testIds.showMirrorAiModal}
        >
          <MenuItemWrapper>
            <Icon name="mirror" />
            <span>{t('Mirror')} {activityTypeName}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {canPingForStatus && (
        <MenuItem
          disableRipple
          onClick={getCallback(pingForStatus)}
          data-testid={testIds.pingForStatus}
        >
          <MenuItemWrapper>
            <Icon name="bell" />
            <span>{t('Ping for Status')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}

      {integrations.map((integration) => (
        <MenuItem
          disableRipple
          onClick={getCallback(() => {
            pushToApp(integration);
            close();
          })}
          data-testid={testIds.pushToIntegration}
          key={integration.uid}
        >
          <MenuItemWrapper>
            <Icon name="calendar" />
            <span>{t("meeting.pushToIntegration", { integrationAppName: integration.other_app_name })}</span>
          </MenuItemWrapper>
        </MenuItem>
      ))}

      {canDelete && (
        <MenuItem
          disableRipple
          onClick={getCallback(showDeleteAiModal)}
          data-testid={testIds.showDeleteAiModal}
        >
          <MenuItemWrapper>
            <Icon name="delete" />
            <span>{t('Delete')}</span>
          </MenuItemWrapper>
        </MenuItem>
      )}
    </Menu>
  );
};

export default Actions;
